import React from 'react';

import DOMPurify from 'dompurify';

import { ChakraText } from '@equip.health/ui';

const idPrefix = 'raw';
const idPostfix = 'html';

// Message bodies are untrusted end-user text. The only markup they legitimately
// carry is what renderNewLine/renderLinks inject downstream of Sendbird: <br/>
// and the anchorTag <a>. <mark> is the search highlight from performSearch,
// which has had no production caller since 56d9ad7c7 and is exercised by tests
// only - it stays allowed so reinstating search does not reopen this file.
const ALLOWED_TAGS = ['a', 'br', 'mark'];

// href only. A sender-authored anchor that kept its own target/rel could pair
// target="_blank" with rel="opener" and hand the popup a live window.opener to
// navigate this tab with (reverse tabnabbing); the hook below puts the safe
// pair back on whatever survives. No style either, because DOMPurify does not
// filter CSS property values, so allowing it would let a sender position a
// full-viewport overlay or beacon out via url(). The anchor styling lives in
// linkStyles below instead.
const ALLOWED_ATTR = ['href'];

const SANITIZE_OPTIONS = {
  ALLOWED_ATTR,
  ALLOWED_TAGS,
  // data-*/aria-* are allowed by default and would otherwise bypass ALLOWED_ATTR.
  ALLOW_ARIA_ATTR: false,
  ALLOW_DATA_ATTR: false,
  // Empty so a disallowed tag drops the tag but keeps the text around it.
  // DOMPurify otherwise deletes the contents of <style>, <video>, <math> and
  // friends, which silently truncates any message that types one in prose.
  FORBID_CONTENTS: [],
};

// Hooks are per-instance state, and the shared default export is also used by
// renderMaiMarkdown, whose anchors are deliberately same-tab. Sanitize on an
// instance of our own so forcing target/rel cannot reach another consumer.
const purifier = DOMPurify();

purifier.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noreferrer noopener');
  }
});

const linkStyles = {
  '& a': { color: 'blue' },
};

interface RawHTMLProps {
  content: string;
  id: string;
}

const RawHTML = React.forwardRef<HTMLDivElement, RawHTMLProps>(
  ({ content, id }: RawHTMLProps, ref?: React.ForwardedRef<HTMLDivElement>) => {
    return (
      <ChakraText
        dangerouslySetInnerHTML={{
          __html: purifier.sanitize(content, SANITIZE_OPTIONS),
        }}
        id={`${idPrefix}__${id}__${idPostfix}`}
        ref={ref}
        sx={linkStyles}
      />
    );
  },
);

RawHTML.displayName = 'RawHTML';

export default RawHTML;
